import { nodeGeometry, objectPortPoint } from './graphGeometry.js';

// Extra margin around a node that still counts as a drop on it.
export const GRAPH_DROP_MARGIN = 10;

const contains = (rect, point, margin = GRAPH_DROP_MARGIN) =>
    point.x >= rect.x - margin && point.x <= rect.x + rect.width + margin &&
    point.y >= rect.y - margin && point.y <= rect.y + rect.height + margin;

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Of the ports of one node or object: the first that accepts the wire,
 * nearest to the pointer first; when none does, the nearest one with its
 * reason. With a single port this is simply that port.
 */
function bestPort(candidates, point, check) {
    const ranked = [...candidates]
        .sort((a, b) => distance(a.point, point) - distance(b.point, point))
        .map(candidate => ({ ...candidate, result: check(candidate.endpoint) }));
    return ranked.find(candidate => candidate.result.ok) ?? ranked[0];
}

/**
 * Find the endpoint a wire being drawn would connect to at `point`.
 *
 * Drawing from an output (`start.dir === 'out'`) looks for an input: of a
 * node, then of a UI object; drawing from an input looks for an output. The
 * whole node/object box is the drop zone, not just the small port; with
 * several fitting ports the one that accepts the wire wins. The topmost
 * candidate (drawn last) wins.
 *
 * @param {{x,y}} point
 * @param {object} options
 * @param {object} options.domain   render domain (getNodeSchema, nodeSize)
 * @param {Array}  options.objects  scene objects [{ uuid, rect, ports }]
 * @param {(from, to) => { ok, reason? }} options.canConnect
 * @returns {{ endpoint, point, result: { ok, reason? } } | null}
 */
export function findGraphDropTarget(point, { domain, graph, origin, objects, start, canConnect }) {
    const want = start.dir === 'out' ? 'in' : 'out';
    const check = endpoint => (start.dir === 'out' ? canConnect(start.endpoint, endpoint) : canConnect(endpoint, start.endpoint));
    for (const node of [...graph.nodes].reverse()) {
        const geometry = nodeGeometry(node, origin, domain);
        const ports = (want === 'in' ? geometry.ports.inputs : geometry.ports.outputs) ?? [];
        if (ports.length === 0 || !contains(geometry.rect, point)) continue;
        return bestPort(ports.map(port => ({
            endpoint: { node: node.id, port: port.id },
            point: geometry.point(want, port.id),
        })), point, check);
    }
    for (const object of [...objects].reverse()) {
        const ports = (object.ports ?? []).filter(port => port.dir === want);
        if (ports.length === 0 || !contains(object.rect, point, 0)) continue;
        return bestPort(ports.map(port => ({
            endpoint: { object: object.uuid, port: port.id },
            point: objectPortPoint(object.rect, port.side),
        })), point, check);
    }
    return null;
}
